import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useUndoableDelete } from "@/hooks/use-undoable-delete";
import { apiGet, apiSend } from "@/lib/api-client";
import { logger } from "@/lib/logger";
import { configKeys } from "@/lib/query";
import { type PayPlan, payPlanArraySchema, payPlanSchema } from "@/schemas/config";

type FormData = {
  payPlanCode: string;
  payPlan: string;
  policyNumberPrefix: string;
};

const emptyForm: FormData = {
  payPlanCode: "",
  payPlan: "",
  policyNumberPrefix: "",
};

export function PayPlanPage() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [pendingDelete, setPendingDelete] = useState<PayPlan | null>(null);

  const {
    data: plans,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: configKeys.payPlans,
    queryFn: () => apiGet("/api/config/pay-plans", payPlanArraySchema),
  });

  const createMutation = useMutation({
    mutationFn: (data: FormData) => apiSend("POST", "/api/config/pay-plans", data, payPlanSchema),
    onSuccess: () => {
      toast.success("Pay plan created");
      queryClient.invalidateQueries({ queryKey: configKeys.payPlans });
      setEditingId(null);
      setFormData(emptyForm);
    },
    onError: (error) => {
      logger.error("pay_plan_create_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to create pay plan");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormData }) =>
      apiSend("PUT", `/api/config/pay-plans/${id}`, data, payPlanSchema),
    onSuccess: () => {
      toast.success("Pay plan updated");
      queryClient.invalidateQueries({ queryKey: configKeys.payPlans });
      setEditingId(null);
      setFormData(emptyForm);
    },
    onError: (error) => {
      logger.error("pay_plan_update_failed", { error });
      toast.error(error instanceof Error ? error.message : "Failed to update pay plan");
    },
  });

  const { pendingIds, enqueue: enqueueDelete } = useUndoableDelete<PayPlan>({
    describe: (p) => `Deleted "${p.payPlanCode} — ${p.payPlan}"`,
    commit: (p) => apiSend("DELETE", `/api/config/pay-plans/${p.id}`, undefined, z.unknown()),
    onCommitSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.payPlans });
    },
    onCommitError: (_, error) => {
      logger.error("pay_plan_delete_failed", { error });
    },
  });

  function handleAdd() {
    setEditingId("new");
    setFormData(emptyForm);
  }

  function handleEdit(plan: PayPlan) {
    setEditingId(plan.id);
    setFormData({
      payPlanCode: plan.payPlanCode,
      payPlan: plan.payPlan,
      policyNumberPrefix: plan.policyNumberPrefix ?? "",
    });
  }

  function handleDelete(plan: PayPlan) {
    setPendingDelete(plan);
  }

  function handleSubmit() {
    if (editingId === "new") {
      createMutation.mutate(formData);
    } else if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    }
  }

  function handleCancel() {
    setEditingId(null);
    setFormData(emptyForm);
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  function renderFormRow(key: string) {
    return (
      <tr key={key} className="border-b">
        <td className="py-3 pr-4">
          <Input
            value={formData.payPlanCode}
            onChange={(e) => setFormData({ ...formData, payPlanCode: e.target.value })}
            placeholder="Plan code"
          />
        </td>
        <td className="py-3 pr-4">
          <Input
            value={formData.payPlan}
            onChange={(e) => setFormData({ ...formData, payPlan: e.target.value })}
            placeholder="Pay plan"
          />
        </td>
        <td className="py-3 pr-4">
          <Input
            value={formData.policyNumberPrefix}
            onChange={(e) => setFormData({ ...formData, policyNumberPrefix: e.target.value })}
            placeholder="Prefix"
          />
        </td>
        <td className="py-3">
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSubmit} disabled={isPending}>
              {isPending ? "Saving..." : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  if (isLoading) {
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Pay Plan</h1>
        <div className="mt-4 space-y-2">
          {["sk-0", "sk-1", "sk-2", "sk-3", "sk-4"].map((k) => (
            <div key={k} className="h-9 animate-pulse rounded bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    logger.error("Failed to load pay plans", { error });
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Pay Plan</h1>
        <ErrorState message="Couldn't load pay plans." onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between rounded-md border bg-card p-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Pay Plan</h1>
          <p className="mt-1 text-muted-foreground">
            Manage payment plan options and policy number prefixes.
          </p>
        </div>
        <Button onClick={handleAdd} disabled={editingId !== null}>
          <Plus className="size-4" />
          Add Plan
        </Button>
      </div>

      <Card className="mt-6">
        <CardHeader />
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-3 pr-4 font-medium">Plan Code</th>
                  <th className="pb-3 pr-4 font-medium">Pay Plan</th>
                  <th className="pb-3 pr-4 font-medium">Policy Prefix</th>
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans
                  ?.filter((p) => !pendingIds.has(p.id))
                  .map((plan) =>
                    editingId === plan.id ? (
                      renderFormRow(plan.id)
                    ) : (
                      <tr key={plan.id} className="border-b">
                        <td className="py-3 pr-4 font-mono">{plan.payPlanCode}</td>
                        <td className="py-3 pr-4">{plan.payPlan}</td>
                        <td className="py-3 pr-4 font-mono">{plan.policyNumberPrefix ?? "—"}</td>
                        <td className="py-3">
                          {/* Wrap each disabled button in a span so the native
                            title tooltip still fires — browsers swallow
                            pointer events on disabled <button>s, so a title
                            attribute on the button alone never shows. */}
                          <div className="flex gap-2">
                            <span
                              title={
                                editingId !== null ? "Finish editing the other row first" : "Edit"
                              }
                              className="inline-block"
                            >
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label="Edit"
                                onClick={() => handleEdit(plan)}
                                disabled={editingId !== null}
                              >
                                <Pencil className="size-4" />
                              </Button>
                            </span>
                            <span
                              title={
                                editingId !== null ? "Finish editing the other row first" : "Delete"
                              }
                              className="inline-block"
                            >
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label="Delete"
                                onClick={() => handleDelete(plan)}
                                disabled={editingId !== null}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </span>
                          </div>
                        </td>
                      </tr>
                    ),
                  )}
                {editingId === "new" && renderFormRow("new")}
              </tbody>
            </table>
            {plans?.length === 0 && editingId !== "new" && (
              <p className="py-8 text-center text-muted-foreground">
                No pay plans configured. Click "Add Plan" to create one.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title="Delete pay plan?"
        description={
          pendingDelete
            ? `This will remove "${pendingDelete.payPlanCode} — ${pendingDelete.payPlan}". You'll have 10 seconds to undo from the toast.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (pendingDelete) enqueueDelete(pendingDelete);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
